// The signed-in New design shell (x-ui.app-topbar with x-ui.app-sidebar), in plain JS like the tab bar so every
// page with the top bar gets it without registering an Alpine component. The inline script in the top bar has
// already set html.ui-shelled, the panel and the remembered width before paint; this adds:
// - the Collapse row pinned to the sidebar's bottom: 248px ↔ 72px icons, remembered per user in localStorage
//   (ui-sh-mini:<user id>); it, the logo and the icons never move, only labels come and go. Folding is Bencho's
//   collapsing sidebar (the timing is in ui.css): html.ui-sh-moving for its length, and the open section's pages shrink
//   to nothing as the panel shuts and grow back as it opens, so the rows under them slide rather than jump;
// - section heads: an accordion (one section open at a time); collapsed, they open the pages in a flyout beside the icon
//   (position: fixed, so the sidebar's scroll never clips it), closed by a click outside, Escape or another head;
// - collapsed, each head and link shows its label as a tooltip (data-tip, drawn by ui-tip.js in the top layer).

const KEY = (id) => `ui-sh-mini:${id}`;
const STILL = window.matchMedia('(prefers-reduced-motion: reduce)');
const OUT = 'cubic-bezier(.22,1,.36,1)';

function setup(shell) {
    if (shell.dataset.ready) return;
    shell.dataset.ready = '1';
    const html = document.documentElement;
    const side = shell.querySelector('[data-ui-side]');
    if (!side) return;
    const toggle = side.querySelector('[data-side-toggle]');
    const heads = [...side.querySelectorAll('[data-label]')];
    const nav = side.querySelector('.ui-sh-nav') ?? side;
    let fly = null;
    let moving = 0;
    let anims = [];

    const mini = () => html.classList.contains('ui-sh-mini');

    const closeFly = (refocus = false) => {
        if (!fly) return;
        const sec = fly;
        fly = null;
        sec.removeAttribute('data-fly');
        const kids = sec.querySelector('.ui-sh-kids');
        kids.style.top = '';
        const head = sec.querySelector('[data-side-head]');
        head.setAttribute('aria-expanded', 'false');
        if (refocus) head.focus();
    };

    const sync = () => {
        const on = mini();
        toggle.setAttribute('aria-pressed', on ? 'true' : 'false');
        toggle.setAttribute('aria-label', on ? 'Expand sidebar' : 'Collapse sidebar');
        toggle.setAttribute('data-tip', on ? 'Expand sidebar' : 'Collapse sidebar');
        toggle.querySelector('.ui-sh-l')?.replaceChildren(on ? 'Expand' : 'Collapse');
        if (!on) toggle.removeAttribute('data-tip');
        // collapsed only: the label as a tooltip beside the icon (open, the label is right there)
        heads.forEach((h) => {
            if (on) { h.setAttribute('data-tip', h.dataset.label); h.setAttribute('data-tip-side', 'right'); }
            else { h.removeAttribute('data-tip'); h.removeAttribute('data-tip-side'); }
        });
        if (on) toggle.setAttribute('data-tip-side', 'right'); else toggle.removeAttribute('data-tip-side');
        side.querySelectorAll('[data-side-sec]').forEach((sec) => {
            sec.querySelector('[data-side-head]')?.setAttribute('aria-expanded', !on && sec.hasAttribute('data-open') ? 'true' : 'false');
        });
    };

    // Shutting: the pages fade with the words, then fold up while the panel narrows. Opening: they grow back while it
    // widens and fade in last, with the words.
    const fold = (shut) => {
        const settle = () => {
            anims.forEach((a) => a.cancel());
            anims = [];
            html.classList.remove('ui-sh-moving');
        };
        settle();
        clearTimeout(moving);
        html.classList.add('ui-sh-moving');
        // held until the pages are folded away (shutting) or the last word is back (opening)
        moving = setTimeout(settle, shut ? 600 : 800);
        const kids = [...side.querySelectorAll('[data-side-sec][data-open] > .ui-sh-kids')];
        const tall = shut ? kids.map((k) => k.scrollHeight) : [];
        html.classList.toggle('ui-sh-mini', shut);
        if (STILL.matches) return;
        kids.forEach((k, i) => {
            const h = `${shut ? tall[i] : k.scrollHeight}px`;
            const frames = shut
                ? [{ height: h, opacity: 1, easing: 'ease' }, { height: h, opacity: 0, offset: 0.28, easing: OUT }, { height: '0px', opacity: 0 }]
                : [{ height: '0px', opacity: 0, easing: OUT }, { height: h, opacity: 0, offset: 0.6, easing: 'ease' }, { height: h, opacity: 1 }];
            k.style.overflow = 'hidden';
            k.style.boxSizing = 'border-box';
            const a = k.animate(frames, { duration: shut ? 580 : 560, fill: 'both' });
            a.oncancel = () => { k.style.overflow = ''; k.style.boxSizing = ''; };
            anims.push(a);
        });
    };

    toggle.addEventListener('click', () => {
        closeFly();
        fold(!mini());
        try { localStorage.setItem(KEY(shell.dataset.user), mini() ? '1' : '0'); } catch (e) {}
        sync();
    });

    side.addEventListener('click', (e) => {
        const head = e.target instanceof Element ? e.target.closest('[data-side-head]') : null;
        if (!head) return;
        const sec = head.closest('[data-side-sec]');
        if (mini()) {
            if (fly === sec) return closeFly();
            closeFly();
            fly = sec;
            sec.setAttribute('data-fly', '');
            head.setAttribute('aria-expanded', 'true');
            const kids = sec.querySelector('.ui-sh-kids');
            const r = head.getBoundingClientRect();
            kids.style.top = `${Math.max(8, Math.min(r.top - 8, window.innerHeight - kids.offsetHeight - 8))}px`;
            kids.querySelector('a')?.focus({ preventScroll: true });
            return;
        }
        // An accordion: opening a section closes the others
        const open = !sec.hasAttribute('data-open');
        side.querySelectorAll('[data-side-sec][data-open]').forEach((other) => {
            if (other === sec) return;
            other.removeAttribute('data-open');
            other.querySelector('[data-side-head]')?.setAttribute('aria-expanded', 'false');
        });
        sec.toggleAttribute('data-open', open);
        head.setAttribute('aria-expanded', open ? 'true' : 'false');
    });

    // Tracking's Saved row: folds open and closed, remembered per user (ui-sh-saved:<user id>)
    side.querySelectorAll('[data-side-fold]').forEach((fold) => {
        const sync = () => fold.setAttribute('aria-expanded', fold.hasAttribute('data-open') || html.classList.contains('ui-sh-saved') ? 'true' : 'false');
        sync();
        fold.addEventListener('click', (e) => {
            e.stopPropagation();
            const open = fold.getAttribute('aria-expanded') !== 'true';
            fold.removeAttribute('data-open');
            html.classList.toggle('ui-sh-saved', open);
            try { localStorage.setItem(`ui-sh-saved:${shell.dataset.user}`, open ? '1' : '0'); } catch (err) {}
            sync();
        });
    });

    document.addEventListener('click', (e) => {
        if (fly && e.target instanceof Node && !fly.contains(e.target)) closeFly();
    });
    document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && fly) closeFly(true);
    });
    window.addEventListener('resize', () => closeFly(), { passive: true });
    nav.addEventListener('scroll', () => closeFly(), { passive: true });

    sync();
}

function boot() {
    document.querySelectorAll('[data-ui-shell]').forEach(setup);
}

if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
else boot();
document.addEventListener('livewire:navigated', boot);
